import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  OnDestroy,
  signal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SettingsService, _HttpClient} from '@delon/theme';

const N3_LAUNCH_DATE = new Date('2026-07-16T00:00:00');
const N3_URL = 'https://n3.qne.cloud';
const BANNER_DISMISSED_KEY = 'n3_migration_banner_dismissed';
const QNE_AI_CLOUD_ACCOUNTING = 'QNE_AI_CLOUD_ACCOUNTING';


const BANNER_HEIGHT_PX = 40;
const BANNER_HEIGHT_VAR = '--migration-banner-h';
const BANNER_VISIBLE_CLASS = 'migration-banner-visible';

export type CompanyTier = 'trial' | 'paid';

@Component({
  selector: 'app-migration-banner',
  standalone: false,
  templateUrl: './migration-banner.component.html',
  styleUrls: ['./migration-banner.component.less'],
})
export class MigrationBannerComponent implements OnInit, OnDestroy {
  private settings = inject(SettingsService);
  private http = inject(_HttpClient);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  isVisible = signal(false);
  daysRemaining = signal(0);
  companyTier = signal<CompanyTier>('trial');
  isLoadingRedirect = signal(false);

  get company() {
    return this.settings.user['company'] ?? {};
  }

  readonly tier = computed<CompanyTier>(() => this.companyTier());

  readonly headline = computed(() => {
    const d = this.daysRemaining();
    if (this.tier() === 'paid') {
      return d > 0
        ? `N3 AI Accounting becomes your primary workspace in <strong>${d} DAY${d !== 1 ? 'S' : ''}</strong>.`
        : `N3 AI Accounting is now your primary workspace.`;
    }
    return d > 0
      ? `Experience the future of accounting — N3 AI launches in <strong>${d} DAY${d !== 1 ? 'S' : ''}</strong>.`
      : `N3 AI Accounting is live! Start your smarter accounting journey today.`;
  });

  readonly subtext = computed(() =>
    this.tier() === 'paid'
      ? 'Start using N3 today to experience the power of Quinny AI with smarter analysis, intelligent assistance, and AI-powered business insights.'
      : 'Upgrade to N3 and unlock Quinny AI — your intelligent accounting assistant for smarter analysis and real-time business insights.'
  );

  readonly ctaLabel = computed(() =>
    this.tier() === 'paid' ? 'Start Using N3. →' : 'Try N3 Free. →'
  );

  ngOnInit(): void {
    this.calculateDaysRemaining();
    this.detectCompanyTier();
    this.evaluateVisibility();
  }

  ngOnDestroy(): void {
    this.clearBannerHeightVar();
  }

  // ── Internals ───────────────────────────────────────────────────────────────

  private calculateDaysRemaining(): void {
    const diff = N3_LAUNCH_DATE.getTime() - Date.now();
    this.daysRemaining.set(Math.max(0, Math.ceil(diff / 86_400_000)));
  }

  private detectCompanyTier(): void {
    const isPaid = !!this.company.licensed && !this.company.isSample;
    this.companyTier.set(isPaid ? 'paid' : 'trial');
  }

  /** True on eintegrator.qne.cloud (or localhost:7002 for dev). */
  private get isEIntegrator(): boolean {
    const { hostname, port } = window.location;
    return (
      hostname === 'eintegrator.qne.cloud' ||
      (hostname === 'localhost' && port === '7002')
    );
  }

  private evaluateVisibility(): void {
    if (this.isEIntegrator) {
      this.clearBannerHeightVar();
      return;
    }

    if (localStorage.getItem(BANNER_DISMISSED_KEY) === 'true') {
      this.clearBannerHeightVar();
      return;
    }

    this.http
      .get<Record<string, unknown>>('api/companyprofile/birInfo?_silent=true')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: birInfo => {
          if (this.shouldHideForBirCasOnQca(birInfo)) {
            this.clearBannerHeightVar();
            return;
          }
          this.showBanner();
        },
        error: () => this.showBanner(),
      });
  }

  private shouldHideForBirCasOnQca(birInfo: Record<string, unknown>): boolean {
    const isRegistered =
      birInfo['isBirCasRegistered'] === true ||
      birInfo['isBirCasRegistered'] === 'true';
    return isRegistered && birInfo['casRegisteredSystem'] === QNE_AI_CLOUD_ACCOUNTING;
  }

  private showBanner(): void {
    this.isVisible.set(true);
    afterNextRender(() => this.syncBannerLayout(), {injector: this.injector});
  }

  /** Offset fixed reuse-tab below the rendered banner height. */
  private syncBannerLayout(): void {
    if (!this.isVisible()) {
      this.clearBannerHeightVar();
      return;
    }

    const banner = document.querySelector<HTMLElement>('.migration-banner');
    const height = Math.ceil(banner?.getBoundingClientRect().height ?? BANNER_HEIGHT_PX);
    const root = document.documentElement;

    root.classList.add(BANNER_VISIBLE_CLASS);
    root.style.setProperty(BANNER_HEIGHT_VAR, `${height}px`);
  }

  private hideBanner(): void {
    this.isVisible.set(false);
    this.clearBannerHeightVar();
  }

  private clearBannerHeightVar(): void {
    const root = document.documentElement;
    root.classList.remove(BANNER_VISIBLE_CLASS);
    root.style.removeProperty(BANNER_HEIGHT_VAR);
  }

  // ── User actions ────────────────────────────────────────────────────────────

  dismiss(): void {
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    this.hideBanner();
  }

  goToN3(): void {
    window.open(N3_URL, '_blank', 'noopener');
  }
}
